import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { METERS, formatNumber } from '../../lib/meters';
import styles from './CreditsGuide.module.css';

// Credits and plan limits, rendered from the single meter catalog (lib/meters.js) so the
// numbers here can never drift from what the product and pricing page enforce. One short
// "what uses it" line per meter, keyed to the catalog.
const USES = {
  leads: 'Charged once for each net-new, verified lead that reaches your list.',
  verify: 'Spent when you check the deliverability of an email address.',
  enrich: 'Spent per company row you fill in during list enrichment.',
  website: 'Spent when RepBuddy resolves and validates a company website.',
  ai: 'Drafting and chat are included free on every plan.',
};

export default function CreditsGuide() {
  // Reveal the table once it scrolls into view: the container animates in first, then each
  // row fades in on a short stagger (see the per-row animationDelay below). Reduced-motion
  // users skip straight to the resolved state via the CSS media query.
  const tableRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || revealed) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  // Container settles first (~260ms), then rows begin, ~70ms apart.
  const ROW_BASE_MS = 260;
  const ROW_STEP_MS = 70;

  return (
    <section id="credits" className={styles.section}>
      <div className={styles.inner}>
        <span className={styles.eyebrow}>Credits</span>
        <h2 className={styles.heading}>Credits and plan limits, explained</h2>
        <p className={styles.lede}>
          RepBuddy meters a few resources so you always know what an action costs before you
          run it. The most important rule is simple: you spend a lead credit only on a
          net-new lead that passes verification. A contact with no verified email never
          reaches your list and is never charged.
        </p>

        <div
          ref={tableRef}
          className={`${styles.tableWrap} ${revealed ? styles.revealed : ''}`}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thLeft}>Resource</th>
                <th className={styles.thLeft}>What uses it</th>
                <th className={styles.thNum}>Solo</th>
                <th className={styles.thNum}>Pro</th>
              </tr>
            </thead>
            <tbody>
              {METERS.map((m, i) => (
                <tr key={m.key} style={{ animationDelay: `${ROW_BASE_MS + i * ROW_STEP_MS}ms` }}>
                  <td className={styles.tdLabel}>{m.label}</td>
                  <td className={styles.tdUse}>{USES[m.key]}</td>
                  <td className={styles.tdNum}>{formatNumber(m.solo)}{m.metered ? ' / mo' : ''}</td>
                  <td className={styles.tdNum}>{formatNumber(m.pro)}{m.metered ? ' / mo' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.points}>
          <div className={styles.point}>
            <h3 className={styles.pointTitle}>You see the cost before and after</h3>
            <p className={styles.pointText}>
              Before an action runs, RepBuddy shows the most it could cost and how much of
              your allowance is left. After it finishes, it shows exactly what was used and
              what was not charged, so the estimate and the actual always reconcile.
            </p>
          </div>
          <div className={styles.point}>
            <h3 className={styles.pointTitle}>Running low does not break anything</h3>
            <p className={styles.pointText}>
              If you reach a limit mid-run, RepBuddy pauses and shows you where you stand
              instead of failing silently. Monthly plan allowances reset each cycle. Booster
              credits, if you add them, never expire.
            </p>
          </div>
          <div className={styles.point}>
            <h3 className={styles.pointTitle}>Verification and enrichment are separate</h3>
            <p className={styles.pointText}>
              Cleaning an email list or enriching companies draws on their own allowances, not
              your lead credits. That keeps your lead credits reserved for net-new people.
            </p>
          </div>
        </div>

        <p className={styles.note}>
          Full numbers for every plan live on the{' '}
          <Link className={styles.inlineLink} to="/pricing">pricing page</Link>. Your live
          balance is always visible on your account.
        </p>
      </div>
    </section>
  );
}
